import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { CompanyType } from '@planning/app/api'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { containerHeights, containerTypes } from '@planning/constants'
import { TFnType, useTranslate } from '@tolgee/react'
import {
  CustomInputAdornment,
  useContainerAttributesParameters,
  useMinimalsTheme,
} from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { SwapBodyCreationFormStore } from '../stores/SwapBodyCreationFormStore'

interface Props {
  store: SwapBodyCreationFormStore
  isOperatorDisabled?: boolean
}

export const getUnitNumberErrorText = (error: string, t: TFnType): string => {
  if (!error) return ''

  if (error === 'fieldIsRequired') {
    return t('fieldIsRequired', 'Field is required.')
  }

  if (error === 'invalidUnitNumber') {
    return t('invalidUnitNumber', 'Invalid Unit number. Must match pattern AAAA9999999.')
  }

  return t(error)
}

export const SwapBodyCreationForm: FC<Props> = observer(({ store, isOperatorDisabled }) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const { types: containerTypeParameters } = useContainerAttributesParameters()

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
      >
        <TextField
          label={t('swapBodyNo', 'Swap body no.')}
          value={store.unitNumber.value}
          onChange={e => store.unitNumber.setValue(e.target.value.toUpperCase())}
          fullWidth
          variant='outlined'
          data-cy='unit-number'
          error={!!store.unitNumber.error}
          helperText={getUnitNumberErrorText(store.unitNumber.error!, t)}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
      >
        <CompanyAutocomplete
          label={t('operator', 'Operator')}
          selectedCompanyShortName={store.operator.value}
          dataCy='operator-autocomplete'
          disabled={isOperatorDisabled}
          handleOnChange={op => store.operator.setValue(op?.shortName ?? '')}
          filterByTypes={[CompanyType.ShippingLine]}
          data-cy='container-operator-field'
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
      >
        <TextField
          label={t('length', 'Length')}
          value={store.length.value}
          onChange={e => store.length.setValue(e.target.value.toUpperCase())}
          fullWidth
          variant='outlined'
          data-cy='container-length'
          InputProps={{
            endAdornment: (
              <CustomInputAdornment
                position='end'
                color='secondary'
                padding={theme.customSpacing.l}
              >
                ft
              </CustomInputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid size={6}>
        <FormControl fullWidth>
          <InputLabel>{t('height', 'Height')}</InputLabel>
          <Select
            label='Height'
            value={store.height.value}
            onChange={(e: SelectChangeEvent) => store.height.setValue(e.target.value)}
            data-cy='container-height'
          >
            {containerHeights.map(d => (
              <MenuItem key={d.key} value={d.name}>
                {d.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid size={12}>
        <FormControl fullWidth>
          <InputLabel>{t('unitType', 'Unit Type')}</InputLabel>
          <Select
            label={`${t('unitType', 'Unit Type')} *`}
            value={store.type.value}
            onChange={(e: SelectChangeEvent) => store.type.setValue(e.target.value)}
            data-cy='container-type'
          >
            {containerTypes
              ?.filter(d => containerTypeParameters[d.name]?.label)
              .map(d => (
                <MenuItem key={d.key} value={d.name}>
                  {containerTypeParameters[d.name].label}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  )
})
